// src/components/SubscriptionManager.js
import { useState, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import PricingModal from './PricingModal';
import { Capacitor } from '@capacitor/core';

import DiamondIcon from '../assets/Diamond Default.png';

export default function SubscriptionManager({ user }) {
  const subscription = useSubscription(user);
  const [showPricing, setShowPricing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check for success/cancel parameters in URL
    const params = new URLSearchParams(window.location.search);
    
    if (params.get('success') === 'true') {
      setMessage('ðŸŽ‰ Subscription activated! Welcome to Premium!');
      subscription.refetch();
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('canceled') === 'true') {
      setMessage('Subscription canceled. You can try again anytime.');
      // Clear URL parameters
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [subscription]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleManageSubscription = async () => {
    if (!user?.email) {
      alert('Unable to manage subscription: User email not found');
      return;
    }

    setLoading(true);
    setMessage('');
    
    try {
      // Determine the correct API URL based on platform
      const isNative = Capacitor.isNativePlatform();
      const apiUrl = isNative 
        ? 'https://iron-path.com//.netlify/functions/create-portal-session'
        : '/.netlify/functions/create-portal-session';

      const returnUrl = `${window.location.origin}${window.location.pathname}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          returnUrl: returnUrl
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create portal session');
      }

      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe Customer Portal
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned');
      }
    } catch (err) {
      console.error('Error managing subscription:', err);
      setMessage(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (subscription.loading) {
    return (
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
          <span className="ml-3 text-purple-300">Loading subscription...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 p-6">
        <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
          <img src={DiamondIcon} alt="Diamond" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/>
          Subscription
        </h2>

        {message && (
          <div className="mb-6 bg-green-500/20 border border-green-500/50 rounded-lg p-4">
            <p className="text-green-200">{message}</p>
          </div>
        )}

        {subscription.isPremium ? (
          // Premium User View
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">
                    {subscription.tier === 'premium_annual' ? 'Premium Annual' : 'Premium Monthly'}
                  </h3>
                  <p className="text-purple-300 text-sm">
                    Status: <span className="text-green-400 font-semibold">Active</span>
                  </p>
                </div>
                <div className="bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold">
                  {subscription.tier === 'premium_annual' ? '£50/year' : '£5/month'}
                </div>
              </div>

              {subscription.endDate && (
                <p className="text-purple-200 text-sm">
                  Renews on: {formatDate(subscription.endDate)}
                </p>
              )}
            </div>

            {/* Premium Features */}
            <div className="bg-white/5 rounded-xl p-6">
              <h4 className="text-lg font-bold text-white mb-4">Your Premium Benefits:</h4>
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center text-purple-200">
                  <span className="text-green-400 mr-2">✓</span>
                  Unlimited AI analyses
                </div>
                <div className="flex items-center text-purple-200">
                  <span className="text-green-400 mr-2">✓</span>
                  Full workout history
                </div>
                <div className="flex items-center text-purple-200">
                  <span className="text-green-400 mr-2">✓</span>
                  Advanced analytics
                </div>
                <div className="flex items-center text-purple-200">
                  <span className="text-green-400 mr-2">✓</span>
                  Unlimited tracking
                </div>
                <div className="flex items-center text-purple-200">
                  <span className="text-green-400 mr-2">✓</span>
                  Export data
                </div>
                <div className="flex items-center text-purple-200">
                  <span className="text-green-400 mr-2">✓</span>
                  Priority support
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleManageSubscription}
                disabled={loading}
                className="bg-purple-500/80 hover:bg-purple-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50"
              >
                Manage Subscription
              </button>
              
              {subscription.tier === 'premium_monthly' && (
                <button
                  onClick={() => setShowPricing(true)}
                  className="bg-green-500/80 hover:bg-green-500 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
                >
                  Upgrade to Annual (Save 17%)
                </button>
              )}
            </div>
          </div>
        ) : (
          // Free User View
          <div className="space-y-6">
            <div className="bg-white/5 rounded-xl p-6 border border-white/10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Free Tier</h3>
                  <p className="text-purple-300 text-sm">
                    You're currently on the free plan
                  </p>
                </div>
                <div className="bg-white/10 text-purple-300 px-4 py-2 rounded-lg font-semibold">
                  £0/month
                </div>
              </div>

              <div className="space-y-2 text-sm text-purple-400">
                <div>• Limited to 2 AI coach analyses per month</div>
                <div>• 30 days workout history only</div>
                <div>• Basic body measurements (last 3 entries)</div>
                <div>• Current day nutrition tracking only</div>
              </div>
            </div>

            {/* Upgrade CTA */}
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/50 rounded-xl p-6">
              <h4 className="text-lg font-bold text-white mb-3 flex items-center">
                <img src={DiamondIcon} alt="Diamond" className="w-8 h-8 rounded-md object-cover mr-2 sm:mr-3"/> Upgrade to Premium
              </h4>
              <p className="text-purple-200 mb-4 text-sm">
                Get unlimited access to all features, advanced analytics, and personalized AI coaching.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">£5</div>
                  <div className="text-purple-300 text-sm">per month</div>
                </div>
                <div className="bg-white/10 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">£50</div>
                  <div className="text-purple-300 text-sm">per year</div>
                  <div className="text-green-400 text-xs mt-1">Save £10!</div>
                </div>
              </div>

              <button
                onClick={() => setShowPricing(true)}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
              >
                View Premium Plans
              </button>
            </div>

            {/* Feature Comparison */}
            <div className="bg-white/5 rounded-xl p-6">
              <h4 className="text-lg font-bold text-white mb-4">What You'll Get with Premium:</h4>
              <div className="space-y-2 text-sm text-purple-200">
                <div className="flex items-center">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span><strong>Unlimited AI analyses</strong> - Get insights whenever you need them</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span><strong>Full workout history</strong> - Track your entire fitness journey</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span><strong>Advanced analytics</strong> - Deeper insights into your progress</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span><strong>Export your data</strong> - Download reports as PDF or CSV</span>
                </div>
                <div className="flex items-center">
                  <span className="text-green-400 mr-2 text-lg">✓</span>
                  <span><strong>Priority support</strong> - Get help when you need it</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <PricingModal
        isOpen={showPricing}
        onClose={() => setShowPricing(false)}
        user={user}
        currentTier={subscription.tier}
      />
    </>
  );
}